import {
  CustomFieldProfileItem,
  useCreateCustomFieldProfileItemMutation,
  useDeleteCustomFieldProfileItemMutation,
  usePatchCustomFieldProfileItemsByProfileMutation,
  useUpdateCustomFieldProfileItemMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseCustomFieldProfileItemMutationProps = {}

export type UseCustomFieldProfileItemMutationResult = {
  createCustomFieldProfileItem: (customFieldProfileItem: Partial<CustomFieldProfileItem>, callback: EntityMutationCallback<CustomFieldProfileItem> | undefined) => void
  updateCustomFieldProfileItem: (customFieldProfileItem: Partial<CustomFieldProfileItem>, callback: EntityMutationCallback<CustomFieldProfileItem> | undefined) => void
  deleteCustomFieldProfileItem: (customFieldProfileItemId: string, callback: EntityMutationCallback | undefined) => void

  patchCustomFieldProfileItemsByProfile: (profileId: string, items: Partial<CustomFieldProfileItem>[], callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean

  isPatchingByProfile?: boolean
}

export const useCustomFieldProfileItemMutation = (props: UseCustomFieldProfileItemMutationProps = {}): UseCustomFieldProfileItemMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  const [_isPatchingByProfile, _setIsPatchingByProfile] = useDebouncedState(false, 500)

  // possible mutations
  const [_createCustomFieldProfileItem, {isLoading: isCreating}] = useCreateCustomFieldProfileItemMutation()
  const [_updateCustomFieldProfileItem, {isLoading: isUpdating}] = useUpdateCustomFieldProfileItemMutation()
  const [_deleteCustomFieldProfileItem, {isLoading: isDeleting}] = useDeleteCustomFieldProfileItemMutation()

  const [_patchCustomFieldProfileItemsByProfile, {isLoading: isPatchingByProfile}] = usePatchCustomFieldProfileItemsByProfileMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    _setIsPatchingByProfile(isPatchingByProfile)
  }, [isPatchingByProfile, _setIsPatchingByProfile])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useCustomFieldProfileItemUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.customfieldprofileitem", "Custom Field Profile Item"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useCustomFieldProfileItemUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useCustomFieldProfileItemUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.customfieldprofileitem", "Custom Field Profile Item"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useCustomFieldProfileItemUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useCustomFieldProfileItemUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.customfieldprofileitem", "Custom Field Profile Item"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useCustomFieldProfileItemUpdate_deleting")
    }
  }, [_deleting, t]);

  useEffect(() => {
    if (_isPatchingByProfile) {
      notifications.show({
        id: "useCustomFieldProfileItemUpdate_patchingByProfile",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.customfieldprofileitem", "Custom Field Profile Items"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useCustomFieldProfileItemUpdate_patchingByProfile")
    }
  }, [_isPatchingByProfile, t]);

  const handleCreateResult = useCallback((promise: Promise<CustomFieldProfileItem>, callback: EntityMutationCallback<CustomFieldProfileItem> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.customfieldprofileitem", "Custom Field Profile Item") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<CustomFieldProfileItem>, callback: EntityMutationCallback<CustomFieldProfileItem> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.customfieldprofileitem", "Custom Field Profile Item") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.customfieldprofileitem", "Custom Field Profile Item") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handlePatchByProfileResult = useCallback((promise: Promise<CustomFieldProfileItem[]>, callback: EntityMutationCallback<CustomFieldProfileItem[]> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.customfieldprofileitem", "Custom Field Profile Item") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((customFieldProfileItemId: string, callback: EntityMutationCallback<CustomFieldProfileItem> | undefined) => {
    const promise = _deleteCustomFieldProfileItem({id: customFieldProfileItemId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteCustomFieldProfileItem, handleDeleteResult])

  const createCustomFieldProfileItem = useCallback((customFieldProfileItem: Partial<CustomFieldProfileItem>, callback: EntityMutationCallback<CustomFieldProfileItem> | undefined) => {
    const promise = _createCustomFieldProfileItem(customFieldProfileItem).unwrap()
    handleCreateResult(promise, callback)
  }, [_createCustomFieldProfileItem, handleCreateResult])

  const updateCustomFieldProfileItem = useCallback((customFieldProfileItem: Partial<CustomFieldProfileItem>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateCustomFieldProfileItem(customFieldProfileItem).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateCustomFieldProfileItem, handleUpdateResult])

  const deleteCustomFieldProfileItem = useCallback((customFieldProfileItemId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.customfieldprofileitem", "Custom Field Profile Item"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.customFieldProfileItem.confirm", "Are you sure you want to delete this custom field profile item?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(customFieldProfileItemId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  const patchCustomFieldProfileItemsByProfile = useCallback((profileId: string, items: Partial<CustomFieldProfileItem>[], callback: EntityMutationCallback | undefined) => {
    const promise = _patchCustomFieldProfileItemsByProfile({profileId, items}).unwrap()
    handlePatchByProfileResult(promise, callback)
  }, [_patchCustomFieldProfileItemsByProfile, handlePatchByProfileResult])

  return {
    createCustomFieldProfileItem: createCustomFieldProfileItem,
    updateCustomFieldProfileItem: updateCustomFieldProfileItem,
    deleteCustomFieldProfileItem: deleteCustomFieldProfileItem,
    patchCustomFieldProfileItemsByProfile: patchCustomFieldProfileItemsByProfile,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
    isPatchingByProfile: _isPatchingByProfile,
  }
}
